import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  VehicleModelDetails,
  VehicleModelLocationDetails,
  ReviewSectionWrapper,
} from 'views/VehicleModelView/components';
import { VehicleModelViewSkeleton } from 'views/VehicleModelView/VehicleModelViewSkeleton';
import axios, { API } from 'lib/config/axios';
import { GA_EVENT_CATEGORY } from 'lib/utils/CONSTANTS';
import {
  setGoogleAnalytics,
  setHrefLangData,
} from 'redux/features/globalData-slice';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import PATHS from 'lib/navigation/paths';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import generateMetaData from 'lib/utils/generateMetaData';
import {
  BikeTechnicalDetails,
  ActionBar,
  ReviewSectionSkeleton,
  LazyLoad,
} from 'views/common/components';
import { Container, Box, Button } from '@mui/material';
import Typography500 from 'views/common/components/UI/Typography500';
import dynamic from 'next/dynamic';
import StyledBox from 'views/VehicleModelView/VehicleModelView.styles';
import { hotjar } from 'react-hotjar';
import getConfig from 'next/config';
import generateVMHrefLangs from 'views/VehicleModelView/utils/generateVMHrefLangs';
import useGetLayout from 'views/common/hooks/useGetLayout';

const MainWidgetDialog = dynamic(() => import('views/common/components/UI/MainWidgetDialog').then(
  (file) => file.MainWidgetDialog,
));

const {
  publicRuntimeConfig: { envVars },
} = getConfig();

const VehicleModelView = (props) => {
  const { slugs, serverData, initWidgetData } = props;
  const dispatch = useDispatch();
  const { googleAnalytics, isSSR } = useSelector((state) => state.globalData);
  const { hreflangData } = useSelector((state) => state.globalData);
  const { getPageData } = useGetLayout();

  const [data, setData] = useState(serverData);
  const appNavigator = useAppNavigator();
  const handleErrorResponse = useErrorsHandler();
  const [openMainWidgetDialog, setOpenMainWidgetDialog] = useState(false);
  const handleCloseMainWidgetDialog = () => {
    setOpenMainWidgetDialog(false);
  };
  const handleOpenMainWidgetDialog = () => {
    setOpenMainWidgetDialog(true);
  };

  const { t } = useTranslation('fe_er_vehicle_model_page');
  const breadcrumbsObject = {
    links: [
      {
        text: t('fe_er_vehicle_model_page:eagle_rider'),
        path: PATHS.home(),
      },
      {
        text: t('fe_er_vehicle_model_page:motorcycle_rentals'),
        path: PATHS.motorcycleRental(),
      },
      {
        text: data?.location?.name,
        path: PATHS.locationSlug(appNavigator.query.slug_1),
      },
      {
        text: t('fe_er_vehicle_model_page:vehicle_model_name_with_location', {
          vehicle_model_name: data?.vehicle_model?.name,
          location_name: data?.location?.name,
        }),
        path: '',
      },
    ],
  };
  const getParams = () => {
    if (slugs.slugs.length === 2) {
      return {
        params: {
          location_id: slugs.slugs[0].resource_id,
          vehicle_model_id: slugs.slugs[1].resource_id,
        },
      };
    }
    return {
      params: {
        location_id: slugs.slugs[0].resource_id,
        vehicle_make_id: slugs.slugs[1].resource_id,
        vehicle_model_id: slugs.slugs[2].resource_id,
      },
    };
  };

  const getData = async () => {
    try {
      const [, res] = await getPageData(() => axios.get(API.vehicleModel.index, getParams()));
      if (res.data.page_configuration.hotjar_enabled) {
        hotjar.initialize(envVars.HOTJAR_SITE_ID, 6);
      }
      const hrefLangs = generateVMHrefLangs(
        res.data,
        appNavigator.locale,
        [slugs.slugs[0].slug, slugs.slugs[1].slug, slugs.slugs[2]?.slug]
          .filter((item) => !!item)
          .join('/'),
      );
      dispatch(setHrefLangData(hrefLangs));
      setData(res.data);
    } catch (err) {
      switch (err.response.status) {
        case 302:
          if (err.response.data.error_code === 'ER0001') {
            appNavigator.push(
              PATHS.vehicleModel.index(
                slugs.slugs[0].slug,
                err.response.data.new_vehicle_make.slug,
                slugs.slugs[1].slug,
              ),
              undefined,
              { translate: false },
            );
          }
          break;
        case 403:
          if (err.response.data.error_code === 'ER0001') {
            appNavigator.push(PATHS.motorcycleRental());
          }
          break;
        default:
          handleErrorResponse(err, false);
      }
    }
  };
  const prepareVehicleModelsList = () => ({
    title: t('fe_er_vehicle_model_page:view_more_models'),
    menuTitle: t('fe_er_vehicle_model_page:choose_model'),
    list: data?.vehicle_models,
  });
  useEffect(() => {
    if (!isSSR) {
      getData();
    }
  }, [slugs]);

  useEffect(() => {
    if (isSSR) {
      setData(serverData);
    }
  }, [serverData]);

  const handleChangeModelClick = (e) => {
    const vehicleModel = data?.vehicle_models.find(
      (model) => model.name === e.target.value,
    );
    // const newSlugsData = [
    //   { ...slugs.slugs[0] },
    //   {
    //     ...slugs.slugs[1],
    //     resource_id: vehicleModel.vehicle_make.id,
    //     slug: vehicleModel.vehicle_make.slug,
    //   },
    //   {
    //     ...slugs.slugs[2],
    //     resource_id: vehicleModel.id,
    //     slug: vehicleModel.slug,
    //   },
    // ];
    // handleSlugTypeChange({ slugs: newSlugsData });

    appNavigator.push(
      PATHS.vehicleModel.index(
        appNavigator.query.slug_1,
        vehicleModel.vehicle_make.slug,
        vehicleModel.slug,
      ),
      null,
      { translate: false },
    );
    setData(null);
  };
  const setReviews = (reviews) => {
    setData({
      ...data,
      users_reviews: reviews,
    });
  };

  useEffect(() => {
    dispatch(
      setGoogleAnalytics({
        ...googleAnalytics,
        newsLetterEventCategory: GA_EVENT_CATEGORY.vehicle_model,
      }),
    );
  }, []);

  return (
    <>
      {data ? (
        <StyledBox>
          <Container maxWidth="xl">
            {data.seo_values && (
              <Head>
                {generateMetaData(data.seo_values)}
                {hreflangData && !serverData && (
                  <>
                    <link rel="canonical" href={hreflangData?.canonical} />
                    {hreflangData.urls?.map((local) => (
                      <link
                        key={local.hreflang}
                        rel="alternate"
                        href={local.url}
                        hrefLang={local.hreflang}
                      />
                    ))}
                  </>
                )}
              </Head>
            )}
            <VehicleModelDetails
              breadcrumbsObject={breadcrumbsObject}
              image={{
                src: data?.vehicle_model?.image.large_url,
                alt: 'vehicle model',
              }}
              description={data?.vehicle_model?.description}
              vehicleModelName={data?.vehicle_model?.name}
              locationName={data?.location?.name}
              youtubeId={data?.vehicle_model?.youtube_video_id}
              vehicleModelsList={prepareVehicleModelsList()}
              vehicleModelLocationId={data?.location?.id}
              vehicleModelId={data?.vehicle_model?.id}
              rateRanges={data?.vehicle_model?.rate_ranges}
              handleChangeModelClick={handleChangeModelClick}
              vehicleModelRatings={data?.vehicle_model?.ratings}
              rentalWidgetInitData={initWidgetData}
            />

            <VehicleModelLocationDetails locationInfo={data?.location} />
            {data?.vehicle_model?.technical_details?.length > 0 && (
              <Box mb={6}>
                <Typography500
                  component="h2"
                  variant="h3"
                  className="VMsecDark">
                  {t('fe_er_vehicle_model_page:technical_details')}
                </Typography500>

                <BikeTechnicalDetails
                  data={data?.vehicle_model?.technical_details}
                />
              </Box>
            )}
            <LazyLoad skeleton={ReviewSectionSkeleton} dataLoaded={data}>
              <ReviewSectionWrapper
                reviewsData={data?.users_reviews}
                vehicleModelTitle={data?.vehicle_model?.name}
                vehicleModelId={data?.vehicle_model?.id}
                getData={getData}
                setReviews={setReviews}
                extraParam={{
                  vehicle_make_slug: appNavigator.query.slug_2,
                }}
              />
            </LazyLoad>
          </Container>
          <Box className="showTABLET">
            <ActionBar>
              <Container maxWidth="xl">
                <Button
                  variant="contained"
                  className="RVBookBtn"
                  fullWidth
                  onClick={() => handleOpenMainWidgetDialog()}>
                  {t('fe_er_vehicle_model_page:book_now')}
                </Button>
              </Container>
            </ActionBar>
          </Box>
          {openMainWidgetDialog && (
            <MainWidgetDialog
              open={openMainWidgetDialog}
              handleClose={handleCloseMainWidgetDialog}
              reservationData={{
                vehicle_model_id: data?.vehicle_model?.id,
                pickup_location_id: data.location.id,
                dropoff_location_id: data.location.id,
              }}
              renderFullWidgetByForce
            />
          )}
        </StyledBox>
      ) : (
        <VehicleModelViewSkeleton />
      )}
    </>
  );
};

export { VehicleModelView };
